import type { CSSProperties } from 'react';
import { isFuturePickup } from '../../filterUtils';
import { MOVE_TYPES, ROW_TYPES, TRAILER_SLOTS } from '../constants';
import {
  TRAILER_MOVES_PANEL_FILTERS,
} from '../Filter/constants';
import type { TrailerMoveFilterOption, SwapBoardTrailer, TrailerMoveMasterRow } from '../types';
import type { AppTheme } from '../styles';
import {
  flattenMasterRowChildren,
  isMasterOrHeaderRow,
  isTrailerMoveMasterRow,
  normalizeMoveType,
} from '../utils';

const normalizePickupDate = (row?: SwapBoardTrailer | null) => {
  if (row?.pickupDate) {
    return row.pickupDate;
  }

  const readyTime = row?.readyTime;
  if (typeof readyTime === 'string' && readyTime.includes('T')) {
    return readyTime.split('T')[0];
  }

  return readyTime ?? null;
};

export const toTrailerMovePickupFilterShape = (row?: SwapBoardTrailer | null) => ({
  driverId: row?.driverId ?? null,
  pickupDate: normalizePickupDate(row),
  isCompleted: row?.isCompleted,
  isMissed: row?.isMissed,
  isVoided: row?.isVoided,
  isNoFreight: row?.isNoFreight,
  isToBeReviewed: row?.isToBeReviewed,
  isAcknowledged: row?.isAcknowledged,
});

export const toTrailerMoveSelection = (row?: SwapBoardTrailer | TrailerMoveMasterRow | null, terminalOffset?: string) => {
  if (!row || isMasterOrHeaderRow(row)) {
    return null;
  }

  const childRow = row as SwapBoardTrailer;
  const pickupShape = toTrailerMovePickupFilterShape(childRow);
  const isFuture =
    pickupShape.pickupDate && terminalOffset
      ? isFuturePickup(pickupShape.pickupDate, terminalOffset)
      : false;

  return {
    pickupId: childRow.pickupId,
    groupId: childRow.groupId,
    ...pickupShape,
    isFuture,
  };
};

const SEARCH_FIELDS = [
  'shipperName',
  'pickupNumber',
  'moveType',
  TRAILER_SLOTS.TRAILER_1,
  TRAILER_SLOTS.TRAILER_2,
  'dollyNumber',
  'driverName',
  'rmaxShipperCode',
];

export const normalizeGroupId = (groupId?: string | null) => groupId?.toUpperCase?.() ?? groupId;

export const hasSpottedTrailer = (row?: SwapBoardTrailer | TrailerMoveMasterRow | null) => {
  if (!row || !isMasterOrHeaderRow(row)) {
    return false;
  }

  return Number(row.spottedTrailers) > 0;
};

const childPassesHeaderAndSearch = ({
  child,
  headerFilter,
  searchText,
  terminalOffset,
}: {
  child: SwapBoardTrailer;
  headerFilter: TrailerMoveFilterOption | undefined;
  searchText: string | undefined;
  terminalOffset: string | undefined;
}) =>
  doesHeaderFilterPass(child, headerFilter, terminalOffset) &&
  searchInTrailerMoveRow(child, searchText);

const sumSpottedTrailersFromMasters = ({
  allRows,
  headerFilteredRows,
  headerFilter,
  terminalOffset,
  isColumnFilterPresent,
}: {
  allRows: TrailerMoveMasterRow[];
  headerFilteredRows: SwapBoardTrailer[];
  headerFilter: TrailerMoveFilterOption | undefined;
  terminalOffset: string | undefined;
  isColumnFilterPresent: boolean | undefined;
}) => {
  if (!allRows?.length || !isTrailerMoveMasterRow(allRows[0])) {
    return 0;
  }

  return allRows
    .filter(isTrailerMoveMasterRow)
    .filter((masterRow) => {
      const children = masterRow.trailers ?? [];
      if (isColumnFilterPresent) {
        return children.some((child) =>
          headerFilteredRows.some((filteredRow) => filteredRow.pickupId === child.pickupId)
        );
      }
      return children.some((child) => doesHeaderFilterPass(child, headerFilter, terminalOffset));
    })
    .reduce((sum, masterRow) => sum + Number(masterRow.spottedTrailers ?? 0), 0);
};

export const searchInTrailerMoveRow = (row: SwapBoardTrailer, searchText?: string) => {
  if (!searchText?.trim()) {
    return true;
  }

  const normalizedSearch = searchText.trim().toLowerCase();
  return SEARCH_FIELDS.some((field) =>
    String(row[field] ?? '')
      .toLowerCase()
      .includes(normalizedSearch)
  );
};

export const doesHeaderFilterPass = (
  row: SwapBoardTrailer | TrailerMoveMasterRow,
  headerFilter: TrailerMoveFilterOption | undefined,
  terminalOffset?: string
) => {
  if (!headerFilter?.filterFunc) {
    return true;
  }

  if (isMasterOrHeaderRow(row)) {
    return true;
  }

  const childRow = row as SwapBoardTrailer;
  return headerFilter.filterFunc({
    pickup: toTrailerMovePickupFilterShape(childRow),
    terminalOffset,
    duplicatePickups: [],
  });
};

export const doesPanelFilterPass = (row: SwapBoardTrailer | TrailerMoveMasterRow, panelFilter?: string) => {
  if (!panelFilter || panelFilter === TRAILER_MOVES_PANEL_FILTERS.all) {
    return true;
  }

  if (isMasterOrHeaderRow(row)) {
    return true;
  }

  const childRow = row as SwapBoardTrailer;
  switch (panelFilter) {
    case TRAILER_MOVES_PANEL_FILTERS.swaps:
      return normalizeMoveType(childRow.moveType) === MOVE_TYPES.SWAP;
    case TRAILER_MOVES_PANEL_FILTERS.drops:
      return normalizeMoveType(childRow.moveType) === MOVE_TYPES.DROP;
    case TRAILER_MOVES_PANEL_FILTERS.hooks:
      return normalizeMoveType(childRow.moveType) === MOVE_TYPES.HOOK;
    case TRAILER_MOVES_PANEL_FILTERS.spottedTrailers:
      return true;
    default:
      return true;
  }
};

export const doesTrailerMovePassFilters = ({
  row,
  headerFilter,
  panelFilter,
  searchText,
  terminalOffset,
}: {
  row: SwapBoardTrailer | TrailerMoveMasterRow;
  headerFilter?: TrailerMoveFilterOption;
  panelFilter?: string;
  searchText?: string;
  terminalOffset?: string;
}) => {
  if (isMasterOrHeaderRow(row)) {
    return true;
  }

  return (
    doesHeaderFilterPass(row, headerFilter, terminalOffset) &&
    doesPanelFilterPass(row, panelFilter) &&
    searchInTrailerMoveRow(row as SwapBoardTrailer, searchText)
  );
};

export const getChildRows = (allRows: (SwapBoardTrailer | TrailerMoveMasterRow)[]): SwapBoardTrailer[] => {
  if (!allRows?.length) {
    return [];
  }
  // MasterDetail injects `_isDetail` clones under expanded rows; exclude them so panel
  // counts do not double when a column filter is active.
  const rowsWithoutDetailPanels = allRows.filter((row) => !row._isDetail);
  if (!rowsWithoutDetailPanels.length) {
    return [];
  }
  if (isTrailerMoveMasterRow(rowsWithoutDetailPanels[0])) {
    return flattenMasterRowChildren(rowsWithoutDetailPanels as TrailerMoveMasterRow[]);
  }
  return rowsWithoutDetailPanels.filter(
    (row) => row.rowType !== ROW_TYPES.GROUP_HEADER
  ) as SwapBoardTrailer[];
};

export const getRowsForGroup = (
  allRows: (SwapBoardTrailer | TrailerMoveMasterRow)[],
  groupId?: string
): SwapBoardTrailer[] => {
  const normalizedGroupId = normalizeGroupId(groupId);
  if (allRows?.length && isTrailerMoveMasterRow(allRows[0])) {
    const masterRow = allRows.find(
      (row) => normalizeGroupId(row.rmaxShipperCode) === normalizedGroupId
    ) as TrailerMoveMasterRow | undefined;
    return masterRow?.trailers ?? [];
  }
  return getChildRows(allRows).filter(
    (row) => normalizeGroupId(row.shipperGroupId ?? row.groupId) === normalizedGroupId
  );
};

const getGroupChildren = (
  row: SwapBoardTrailer | TrailerMoveMasterRow,
  allRows: TrailerMoveMasterRow[]
): SwapBoardTrailer[] =>
  isTrailerMoveMasterRow(row) ? (row.trailers ?? []) : getRowsForGroup(allRows, row.groupId ?? row.id);

export const doesExternalFilterPass = ({
  node,
  allRows,
  headerFilter,
  panelFilter,
  searchText,
  terminalOffset,
}: {
  node: { data?: SwapBoardTrailer | TrailerMoveMasterRow };
  allRows: TrailerMoveMasterRow[];
  headerFilter?: TrailerMoveFilterOption;
  panelFilter?: string;
  searchText?: string;
  terminalOffset?: string;
}) => {
  const row = node.data;
  if (!row) {
    return false;
  }

  if (row._isDetail) {
    return true;
  }

  if (isMasterOrHeaderRow(row)) {
    if (panelFilter === TRAILER_MOVES_PANEL_FILTERS.spottedTrailers) {
      return (
        hasSpottedTrailer(row) &&
        getGroupChildren(row, allRows).some((child) =>
          childPassesHeaderAndSearch({ child, headerFilter, searchText, terminalOffset })
        )
      );
    }

    return getGroupChildren(row, allRows).some((child) =>
      doesTrailerMovePassFilters({
        row: child,
        headerFilter,
        panelFilter,
        searchText,
        terminalOffset,
      })
    );
  }

  return doesTrailerMovePassFilters({
    row,
    headerFilter,
    panelFilter,
    searchText,
    terminalOffset,
  });
};

export const constructStatusBar = ({
  allRows,
  headerFilter,
  filteredData,
  isColumnFilterPresent,
  terminalOffset,
}: {
  allRows: TrailerMoveMasterRow[];
  headerFilter?: TrailerMoveFilterOption;
  filteredData: (SwapBoardTrailer | TrailerMoveMasterRow)[];
  isColumnFilterPresent?: boolean;
  terminalOffset?: string;
}) => {
  const childRows = getChildRows(allRows);
  const headerFilteredRows = isColumnFilterPresent
    ? getChildRows(filteredData)
    : childRows.filter((row) => doesHeaderFilterPass(row, headerFilter, terminalOffset));

  const swaps = headerFilteredRows.filter(
    (row) => normalizeMoveType((row as SwapBoardTrailer).moveType) === MOVE_TYPES.SWAP
  ).length;
  const drops = headerFilteredRows.filter(
    (row) => normalizeMoveType((row as SwapBoardTrailer).moveType) === MOVE_TYPES.DROP
  ).length;
  const hooks = headerFilteredRows.filter(
    (row) => normalizeMoveType((row as SwapBoardTrailer).moveType) === MOVE_TYPES.HOOK
  ).length;
  const spottedTrailers = sumSpottedTrailersFromMasters({
    allRows,
    headerFilteredRows,
    headerFilter,
    terminalOffset,
    isColumnFilterPresent,
  });

  return [
    {
      label: `${headerFilteredRows.length} Trailer Moves`,
      filterCondition: TRAILER_MOVES_PANEL_FILTERS.all,
    },
    {
      label: `${swaps} Swaps`,
      filterCondition: TRAILER_MOVES_PANEL_FILTERS.swaps,
    },
    {
      label: `${drops} Drops`,
      filterCondition: TRAILER_MOVES_PANEL_FILTERS.drops,
    },
    {
      label: `${hooks} Hooks`,
      filterCondition: TRAILER_MOVES_PANEL_FILTERS.hooks,
    },
    {
      label: `${spottedTrailers} Spotted Trailers`,
      filterCondition: TRAILER_MOVES_PANEL_FILTERS.spottedTrailers,
    },
  ];
};

export const getGroupRowStyle = (theme?: AppTheme): CSSProperties => ({
  backgroundColor: theme?.gridBGColor || 'transparent',
  fontWeight: theme?.typography?.fontWeightMedium || 500,
  transition: 'background-color 0.2s ease',
});
